import { useCallback } from 'react';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useDesign } from '../context/DesignContext';
import { useToast } from '../components/ui/Toast';
import { apiService } from '../services/apiService';
import { TOOL_COSTS } from '../constants';
import { useNavigate } from 'react-router-dom';
import { ToolType } from '../types';

export const useToolExecution = () => {
  const { user } = useAuth();
  const { inputImage, maskData, generalInstruction, redInstruction, greenInstruction, setApiResult, setLoading } = useDesign();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const checkCredits = useCallback((cost: number) => {
    if (!user) { addToast('Please login first!', 'error'); return false; }
    if (user.credits < cost) { 
        addToast(`Insufficient credits! Need ${cost}, have ${user.credits}.`, 'error'); 
        navigate('/dashboard/pricing');
        return false; 
    }
    return true;
  }, [user, addToast, navigate]);

  const deductCredits = useCallback(async (cost: number, toolName: string, description?: string) => { 
      if (!user) return;
      try {
          await db.collection('users').doc(user.uid).update({ credits: user.credits - cost });
          
          try {
              await db.collection('transactions').add({
                  userId: user.uid,
                  credits: -cost,
                  type: 'usage',
                  description: description || `${toolName} Tool Usage`,
                  timestamp: new Date()
              });
          } catch (txError: any) {
              console.error("Transaction Log Failed (Credits were still deducted):", txError);
          }
      } catch (e) {
          console.error("Error deducting credits:", e);
          addToast("Credit deduction failed. Please check internet connection.", 'error');
      }
  }, [user, addToast]);

  const refundCredits = useCallback(async (amount: number, reason: string) => {
      if (!user) return;
      try {
          await db.collection('users').doc(user.uid).update({ credits: user.credits + amount });
          
          try {
              await db.collection('transactions').add({
                  userId: user.uid,
                  credits: amount,
                  type: 'refund',
                  description: `Refund: ${reason}`,
                  timestamp: new Date()
              });
          } catch (txError) {
              console.error("Refund Log Failed:", txError);
          }
          addToast(`Refunded ${amount} credits: ${reason}`, 'info');
      } catch (e) {
          console.error("Error refunding credits:", e);
      }
  }, [user, addToast]);

  const executeTool = useCallback(async (activeTool: ToolType) => {
    setLoading(true);
    setApiResult(null);
    try {
      let result: any;
      const cost = activeTool in TOOL_COSTS ? TOOL_COSTS[activeTool as keyof typeof TOOL_COSTS] : 0;
      if (cost > 0 && !checkCredits(cost)) { setLoading(false); return; }

      switch (activeTool) {
        case 'clone': 
            if (!inputImage) throw new Error("No image selected"); 
            result = await apiService.cloneDesign(inputImage); 
            break;
        case 'redesign': 
            if (!inputImage) throw new Error("Image required"); 
            const imageToSend = maskData?.compositeImage || inputImage;
            result = await apiService.redesign(imageToSend, generalInstruction, redInstruction, greenInstruction, maskData); 
            break;
        case 'embroidery': 
            if (!inputImage) throw new Error("No image selected"); 
            result = await apiService.embroidery(inputImage); 
            break;
        case 'removebg': 
            if (!inputImage) throw new Error("No image selected"); 
            result = await apiService.removeBackground(inputImage); 
            break;
        case 'upscale': 
            if (!inputImage) throw new Error("No image selected"); 
            result = await apiService.upscale(inputImage); 
            break;
        // listing logic is typically separate, handled by ListingOptimizer component?
        // In App.tsx it wasn't in the switch case unless activeTool was 'listing'?
        // Wait, listing logic was separate in App.tsx? Let's check.
      }
      
      if (result && result.success) {
        if (cost > 0) {
            const toolName = activeTool.charAt(0).toUpperCase() + activeTool.slice(1);
            await deductCredits(cost, toolName); 
        }
        setApiResult(result);
        addToast("Tool executed successfully!", 'success');
      } else if (result) { 
          setApiResult({ success: false, error: result.error || "Unknown error occurred" }); 
      }
    } catch (error: any) { 
        setApiResult({ success: false, error: error.message }); 
    } finally { 
        setLoading(false); 
    }
  }, [user, inputImage, maskData, generalInstruction, redInstruction, greenInstruction, checkCredits, deductCredits, setApiResult, setLoading, addToast]);

  return { executeTool, checkCredits, deductCredits, refundCredits };
};
